// frontend/src/context/AuthContext.js
// ----------------------------------------------------
// Authentication Context - Manages user login state
// Uses JWT tokens stored in localStorage
// ----------------------------------------------------

import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../config";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem("jwt") || null);
    const [loading, setLoading] = useState(true);

    // ✅ On mount, check if we have a stored token and validate it
    useEffect(() => {
        const validateToken = async () => {
            const storedToken = localStorage.getItem("jwt");
            if (storedToken) {
                try {
                    // Set token in axios headers
                    axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;

                    // Validate token by checking session
                    const { data } = await axios.get(`${API_BASE}/auth/session`);
                    if (data?.ok) {
                        setToken(storedToken);
                        setUser(data.user || { email: data.email || "User" });
                    } else {
                        // Token invalid, clear it
                        localStorage.removeItem("jwt");
                        delete axios.defaults.headers.common["Authorization"];
                        setToken(null);
                        setUser(null);
                    }
                } catch (err) {
                    console.warn("Token validation failed:", err.message);
                    localStorage.removeItem("jwt");
                    delete axios.defaults.headers.common["Authorization"];
                    setToken(null);
                    setUser(null);
                }
            }
            setLoading(false);
        };

        validateToken();
    }, []);

    // ✅ Login function
    const login = async (email, password) => {
        try {
            const { data } = await axios.post(`${API_BASE}/user/login`, { email, password });

            if (data?.ok && data?.token) {
                localStorage.setItem("jwt", data.token);
                axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
                setToken(data.token);
                setUser(data.user);
                return { success: true };
            } else {
                return { success: false, error: data?.error || "Login failed" };
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message || "Login failed";
            return { success: false, error: errorMsg };
        }
    };

    // ✅ Register function (no token - user needs admin activation)
    const register = async (name, email, password) => {
        try {
            const { data } = await axios.post(`${API_BASE}/user/register`, { name, email, password });

            if (data?.ok) {
                // Registration successful but user needs admin approval
                // Don't set token or user - they can't login yet
                return { success: true, message: data.message };
            } else {
                return { success: false, error: data?.error || "Registration failed" };
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message || "Registration failed";
            return { success: false, error: errorMsg };
        }
    };

    // ✅ Logout function
    const logout = () => {
        localStorage.removeItem("jwt");
        delete axios.defaults.headers.common["Authorization"];
        setToken(null);
        setUser(null);
    };

    const value = {
        user,
        token,
        loading,
        isAuthenticated: !!token,
        login,
        register,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

export default AuthContext;
